"use client";

import { ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import DOMPurify from "dompurify";

interface MessageDisplayProps {
  htmlContent: string;
}

const renderTxt = (text: string) => {
  // To dynamically render text with HTML + tailwind classes
  const sanitizedText = DOMPurify.sanitize(text);
  return { __html: sanitizedText };
};

export function MessageDisplay({ htmlContent }: MessageDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const plainText = renderTxt(htmlContent);
  const truncateThreshold = 150;

  useEffect(() => {
    // Determine if truncation is needed based on plain text length
    const textNoHtml = plainText.__html.replace(/<[^>]*>/g, "");
    if (textNoHtml.length > truncateThreshold) {
      setNeedsTruncation(true);
    } else {
      setNeedsTruncation(false);
    }
  }, [plainText, truncateThreshold]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="relative">
      <div
        ref={contentRef}
        className={
          !isExpanded && needsTruncation
            ? "max-h-[4.5em] overflow-hidden relative"
            : ""
        } // Approx 3 lines of text
        style={{
          // Add a fade effect if truncated
          maskImage:
            !isExpanded && needsTruncation
              ? "linear-gradient(to bottom, black 60%, transparent 100%)"
              : "none",
          WebkitMaskImage:
            !isExpanded && needsTruncation
              ? "linear-gradient(to bottom, black 60%, transparent 100%)"
              : "none",
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </div>
      {needsTruncation && (
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleExpand}
          className="absolute bottom-0 right-0 bg-background/80 backdrop-blur-sm h-auto py-1 px-2 text-xs"
        >
          {isExpanded ? (
            <ChevronUpIcon className="ml-1 h-3 w-3" />
          ) : (
            <ChevronDownIcon className="ml-1 h-3 w-3" />
          )}
        </Button>
      )}
    </div>
  );
}
